/** "Test this agent" — dial a one-off call to any number with the campaign's
 * current agent config, so an operator can hear it before launching. Gated on
 * `campaign.members.manage`; rate-limited + audit-logged server-side. The
 * dialled contact is ephemeral (excluded from the funnel, auto-purged). */
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import PhoneInput from "@/components/PhoneInput";
import { describeApiError } from "@/lib/apiErrors";
import { PermissionGate } from "@/components/PermissionGate";
import { placeAITestCall } from "./api";

export function TestCallPanel({ campaignId }: { campaignId: string }) {
  const { t } = useTranslation();
  const [phone, setPhone] = useState("");
  const [firstName, setFirstName] = useState("");

  const call = useMutation({
    mutationFn: () =>
      placeAITestCall(campaignId, {
        phone,
        first_name: firstName.trim(),
      }),
  });

  return (
    <PermissionGate perm="campaign.members.manage">
      <section className="card space-y-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("aiConsole.testCall.heading", "Test this agent")}
          </h2>
          <p className="text-xs text-nb-text-3">
            {t(
              "aiConsole.testCall.hint",
              "Dial a one-off call to any number using this campaign's current agent — to hear it before launching.",
            )}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr,1fr,auto] sm:items-end">
          <label className="space-y-1 text-xs text-nb-text-2">
            <span>{t("aiConsole.testCall.phone", "Phone to call")}</span>
            <PhoneInput value={phone} onChange={setPhone} />
          </label>
          <label className="space-y-1 text-xs text-nb-text-2">
            <span>{t("aiConsole.testCall.firstName", "First name (optional)")}</span>
            <input
              className="input w-full"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t("aiConsole.testCall.firstNamePlaceholder", "Test")}
            />
          </label>
          <button
            type="button"
            className="btn-primary text-sm"
            disabled={!phone || call.isPending}
            onClick={() => call.mutate()}
          >
            {call.isPending
              ? t("aiConsole.testCall.calling", "Calling…")
              : t("aiConsole.testCall.call", "Call now")}
          </button>
        </div>
        {call.isSuccess && (
          <p className="text-xs text-nb-mint">
            {t("aiConsole.testCall.placed", "Calling {{phone}} now — pick up!", {
              phone: call.data.phone,
            })}
          </p>
        )}
        {call.isError && (
          <p className="text-xs text-cvp-danger">
            {describeApiError(
              call.error,
              t("aiConsole.testCall.error", "Could not place the test call."),
            )}
          </p>
        )}
      </section>
    </PermissionGate>
  );
}
